import {
  useEffect, useRef, useState, ReactElement,
} from 'react';
import { useRouter } from 'next/router';
import {
  Button, Collapse, Chip, Divider,
} from '@mui/material';
import Tune from '@mui/icons-material/Tune';
import Download from '@mui/icons-material/Download';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import ListAlt from '@mui/icons-material/ListAlt';
import CreateIcon from '@mui/icons-material/Create';
import ReorderIcon from '@mui/icons-material/Reorder';
import RefreshIcon from '@mui/icons-material/Refresh';
import { CheckCircle } from '@mui/icons-material';
import { notify } from 'lib/utils/honeybadgerUtils';
import { useModal } from 'mui-modal-provider';
import {
  MilestoneCounts, Pin, Post,
} from 'lib/types';
import { ProfileUser } from 'lib/types/users';
import { Pagination } from 'components/Pagination/ESPagination';
import { ESPaginationInfo, PostServiceStats, PostServiceMeta } from 'server/api/types';
import { useApi, useApplicationState, useCurrentUser } from 'lib/contexts/ApplicationState';
import {
  userIsPro, userIsProfileOwner, userIsProOrAdminOrOG, userCanManageAccount, userIsAdmin, pluralize,
} from 'lib/helpers';
import { trackClickSwitchToMyImports, useViewProfileEvent } from 'lib/hooks/useEventTracking';
import { PostSearchCriteria, PostSort } from 'lib/types/filters';
import {
  queryToCriteria, postSortOptions, basicPostSortOptions,
} from 'lib/utils/filterUtils';
import { useUserRoles } from 'lib/contexts/userRolesContext';
import { isGondolaApp } from 'lib/utils/mobileAppUtils';
import { COLLECTION_KEYS } from 'lib/filters';
import { UserSummary } from 'components/UserSummary';
import { useUpgradeModal } from 'components/UpgradeModal';
import { ImportWizard } from 'components/ImportWizard';
import { FilterPostTitle } from 'components/FilterPostTitle';
import { useSnackbar } from 'notistack';
import { isEqual, omit, isEmpty } from 'lodash';
import { ShareButton } from 'components/ShareButton';
import { SortFilter } from 'components/Filters/SortFilter';
import { PostStats } from 'components/PostStats';
import { QuickAddProject } from 'components/AddEditProject/AddEditProject';
import { Manager } from 'features/Manager/NewManager';
import { useMutation, useQuery } from '@tanstack/react-query';

import { AddDrawer, Method, DrawerType } from 'features/Manager/AddDrawer';
import { AddTags } from 'features/Manager/AddTags';
import { CreditDrawer } from 'features/Manager/CreditDrawer';
import { SelectedPosts } from 'features/Manager/SelectedPosts';
import { MatchDrawer } from 'features/Manager/MatchDrawer';
import { SimilarPosts } from 'features/Manager/SimilarPosts';
import { Credit } from 'lib/types/credits';
import { Tag } from 'lib/types/tags';
import { Modal } from 'components/Modal';
import { ProBadge } from 'components/ProBadge';
import { CreditContributions } from 'components/UserSummary/CreditContributions';

import { FilterCriteria } from './FilterCriteria';
import { FilterPostResult } from './FilterPostResult';
import { Portfolio } from './Portfolio';

interface ChipItem { id?: number; title?: string; type: string }

interface Props {
  title: string;
  richTitle: string | ReactElement;
  profileUser: ProfileUser;
  stats?: PostServiceStats;
  milestoneCounts?: MilestoneCounts;
  totalFollowers?: number;
  serverSidePosts?: Post[] | null;
  serverSidePostMeta?: PostServiceMeta | null;
}

export const Profile = ({
  title, richTitle, profileUser, stats, milestoneCounts, totalFollowers,
  serverSidePosts, serverSidePostMeta,
}: Props) => {
  const API = useApi();
  const router = useRouter();
  const currentUser = useCurrentUser();
  const { isAuthed } = useApplicationState();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();
  const [isProfileOwner, setIsProfileOwner] = useState(false);
  const [canManageAccount, setCanManageAccount] = useState(false);
  const [showProjects, setShowProjects] = useState(true);
  const [showAutoCreditModal, setShowAutoCreditModal] = useState(false);

  const [posts, setPosts] = useState<Post[]>(serverSidePosts || []);
  const [pagingMeta, setPagingMeta] = useState<ESPaginationInfo | undefined>(
    serverSidePostMeta?.paging,
  );
  const [statsMeta, setStatsMeta] = useState<PostServiceStats | undefined>(
    serverSidePostMeta?.stats,
  );
  const [metaError, setMetaError] = useState<Error | undefined>(undefined);
  const [profileUserPins, setProfileUserPins] = useState<Post[]>([]);
  const [_criteria, setCriteria] = useState<PostSearchCriteria>(
    () => queryToCriteria(router.query),
  );
  const [profileUserId, setProfileUserId] = useState(profileUser?.id);
  const [projectChips, setProjectChips] = useState<ChipItem[]>([]);

  const [isImportOpen, setIsImportOpen] = useState(false);
  const { showUpgradeModal } = useUpgradeModal();

  const postView = router.query.postView as string || 'card';

  const [selectedPostIds, setSelectedPostIds] = useState<number[]>([]);
  const [drawer, setDrawer] = useState<DrawerType | undefined>();
  const [toCopyCredits, setToCopyCredits] = useState<Credit[]>([]);
  const [toCopyTags, setToCopyTags] = useState<Tag[]>([]);
  const [method, setMethod] = useState<Method | undefined>();

  const [similarPosts, setSimilarPosts] = useState<Post[]>([]);
  const [selectedSimilarPosts, setSelectedSimilarPosts] = useState<Post[]>([]);

  const [additionalPosts, setAdditionalPosts] = useState<Post[]>([]);
  const [additionalLoading, setAdditionalLoading] = useState(false);
  const [uncreditedTotal, setUncreditedTotal] = useState(0);

  const filterRef = useRef(null);
  const initialCriteria = useRef(_criteria);

  // Only use SSR data as initialData if criteria hasn't changed from initial
  const shouldUseSSRData = serverSidePosts
    && serverSidePostMeta
    && isEqual(_criteria, initialCriteria.current);

  const {
    data: postsData,
    error: postsError,
    isPending,
    isFetching,
  } = useQuery({
    queryKey: ['getProfilePosts', profileUser.id, _criteria],
    queryFn: async () => API.getProfilePosts(profileUser.id, _criteria),
    enabled: isAuthed && router.isReady,
    initialData: shouldUseSSRData ? {
      data: serverSidePosts,
      meta: serverSidePostMeta,
    } : undefined,
  });

  // Update posts when query data arrives
  useEffect(() => {
    if (postsData) {
      const { data, meta } = postsData;
      const profileUserHasChanged = profileUser.id !== profileUserId;

      // Check if criteria changed (excluding pagination fields)
      const criteriaChanged = !isEqual(
        omit(_criteria, ['from']),
        omit(queryToCriteria(router.query), ['from']),
      );

      if (criteriaChanged || profileUserHasChanged) {
        // When criteria changes, start fresh
        setPosts(data);
        setAdditionalPosts([]);
      } else {
        // Pagination: append to existing posts
        setPosts([...posts, ...data]);
      }

      setProfileUserId(profileUser.id);
      setPagingMeta(meta.paging);
      setStatsMeta(meta.stats);

      if (isEmpty(omit(_criteria, 'profileuser'))) {
        setShowProjects(true);
      } else {
        setShowProjects(false);
      }
    }
  }, [postsData]);

  // Handle query errors
  useEffect(() => {
    if (postsError) {
      if (postsError?.name === 'AbortError') {
        return;
      }
      notify(postsError, 'Profile/fetchPosts', {
        queryParams: router.query,
      });
    }
  }, [postsError, router.query]);

  const { data: suggestedCreditCount, error: suggestedCreditCountError } = useQuery({
    queryKey: ['getSuggestedCreditCountByUserId', profileUser.id],
    queryFn: () => API.getSuggestedCreditCountByUserId(profileUser.id),
    enabled: false, // TODO: Pausing this feature for now
  });

  const { data: creditActivity } = useQuery({
    queryKey: ['getUserCreditActivity', profileUser.id],
    queryFn: () => API.getUserCreditActivity(profileUser.id),
    enabled: !!profileUser?.id && !!currentUser?.isAdmin,
  });

  useEffect(() => {
    if (suggestedCreditCount && suggestedCreditCount?.count > 0) {
      setShowAutoCreditModal(true);
    }
  }, [suggestedCreditCount]);

  useEffect(() => {
    if (suggestedCreditCountError) {
      console.log({ suggestedCreditCountError });
    }
  }, [suggestedCreditCountError]);

  const goToSettings = () => {
    router.push('/settings');
  };

  const tryToExportPosts = async () => {
    if (!userIsProOrAdminOrOG(currentUser) && !canManageAccount) {
      showUpgradeModal('Upgrade to export posts');
    } else {
      try {
        await API.exportCSVReportForProfile(profileUser.id, queryToCriteria(router.query));
        enqueueSnackbar('Started your export. We will send you a notification when it is complete.', {
          variant: 'success',
        });
      } catch (err: any) {
        enqueueSnackbar(err?.message, { variant: 'error' });
      }
    }
  };

  const goToMyImports = () => {
    if (currentUser?.id) {
      trackClickSwitchToMyImports(API, profileUser?.id);
    }
    router.push({
      pathname: '/explore',
      query: {
        ownership: 'imported',
        postView: 'list',
        creditedStatus: 'all',
      },
    });
  };

  const handleBulkActionsClick = () => {
    const isOn = router.query.postView === 'list';

    router.push({
      pathname: `/${profileUser.username}`,
      query: {
        ...omit(router.query, 'postView'),
        ...(isOn ? {} : { postView: 'list' }),
      },
    }, undefined, { shallow: true });
  };

  const renderMyImportsButton = () => {
    if (!currentUser) {
      return null;
    }

    return (
      <Button
        title="My Imports"
        aria-label="My Imports"
        onClick={goToMyImports}
      >
        <ListAlt fontSize="small" className="mr-1" />
        My Imports
      </Button>
    );
  };

  const renderBulkActionsButton = () => {
    const isOn = router.query.postView === 'list';
    const buttonClassName = isOn
      ? 'btn-primary-xs uppercase whitespace-nowrap flex items-center gap-1'
      : 'btn-primary-outlined-xs uppercase whitespace-nowrap flex items-center gap-1';

    return (
      <button
        type="button"
        title="Bulk Actions"
        aria-label="Bulk Actions"
        onClick={handleBulkActionsClick}
        className={buttonClassName}
      >
        {isOn ? (
          <CheckCircle fontSize="small" />
        ) : (
          <ReorderIcon fontSize="small" />
        )}
        Bulk Actions
        <ProBadge height={20} />
      </button>
    );
  };

  /** this useEffect handles fetching additional posts,
   * only if the user is on the last page of results */
  useEffect(() => {
    if (!pagingMeta || postView !== 'card'
      || profileUser.hidePersonalPosts || isProfileOwner
    ) {
      return;
    }

    // if there is any criteria (not related to pagination) then return
    if (!isEmpty(omit(router.query, ['from', 'size', 'profileuser', 'creditedStatus', 'sort']))) {
      setAdditionalPosts([]);
      return;
    }

    if (pagingMeta?.nextFrom > pagingMeta?.count) {
      fetchAdditionalPosts(_criteria || {});
    }
  }, [pagingMeta, router.query]);

  const fetchAdditionalPosts = async (criteria: PostSearchCriteria) => {
    setAdditionalLoading(true);
    try {
      const { data, meta } = await API.getProfilePosts(profileUser.id,
        {
          ...criteria, creditedStatus: 'uncredited', size: 4, from: 0,
        });
      setAdditionalPosts(data);
      setUncreditedTotal(meta.stats.totalPosts || 0);
    } catch (err: any) {
      if (err?.name === 'AbortError' || err === 'CANCELLED') {
        // An abort error is because they've already selected new criteria.
        // No need to even show a snackbar.
        return;
      }

      notify(err, 'Profile/fetchAdditionalPosts', {
        queryParams: router.query,
      });
    } finally {
      setAdditionalLoading(false);
    }
  };

  /** This useEffect updates criteria when the router query changes */
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    setMetaError(undefined);

    const newCriteria = queryToCriteria(router.query);
    if (!isEqual(_criteria, newCriteria)) {
      if (!isEqual(
        omit(_criteria, ['from', 'profileuser']),
        omit(newCriteria, ['from', 'profileuser']),
      )) {
        // When criteria other than the `from` changes, clear posts for fresh start
        setPosts([]);
      }
      setCriteria(newCriteria);
    }
  }, [router?.isReady, router?.query, _criteria]);

  // TODO: sync fetchPins w/ fetchPostsWithMeta
  async function fetchPins() {
    const pins = await API.getPinnedPosts(profileUser.id);
    const pinnedPosts = pins?.filter((pin: Pin) => !!pin.post).map((pin: Pin) => pin.post) || [];
    setProfileUserPins(pinnedPosts);
  }
  const profileUserRoles = useUserRoles();
  /** This useEffect sets pins and project chips */
  useEffect(() => {
    const isOnFirstPage = !router.query.page || router.query.page === '1';
    const hasQuery = Object.values(COLLECTION_KEYS).some((k) => k in router.query);

    if (userIsPro(profileUser) && isOnFirstPage && !hasQuery) {
      fetchPins();
    } else {
      setProfileUserPins([]);
    }

    if (userIsAdmin(currentUser)) {
      const topCollabs = profileUser?.collaborators?.slice(0, 3)
        .map((collab) => ({ title: collab.username || collab.name, id: collab.id, type: 'users' })) || [];
      const topRoles = profileUserRoles?.slice(0, 3)
        .map((role) => ({ title: role.name, id: role.id, type: 'role' })) || [];
      setProjectChips([...topCollabs, ...topRoles]);
    }
  }, [currentUser, profileUser, API, router.query]);

  /** This useEffect handles setting if the current user is the profile owner
   * or can manage the account */
  useEffect(() => {
    setIsProfileOwner(userIsProfileOwner(currentUser, profileUser));
    setCanManageAccount(userCanManageAccount(currentUser, profileUser) || userIsAdmin(currentUser));
  }, [currentUser, profileUser]);

  useViewProfileEvent(profileUser.id);

  const showImportAndExport = !isGondolaApp || (isGondolaApp && userIsProOrAdminOrOG(currentUser));

  const [showFilters, setShowFilters] = useState(false);
  const [showProjectChips, setShowProjectChips] = useState(false);

  const chipLabel = (item: ChipItem) => {
    if (item.type === 'users') {
      return `@${item.title}`;
    }
    if (item.type === 'tag') {
      return `#${item.title}`;
    }
    return item.title;
  };

  const chipColor = (type: string): 'primary' | 'secondary' | 'success' | 'default' => {
    switch (type) {
    case 'role':
      return 'secondary';
    case 'tags':
      return 'success';
    case 'users':
      return 'primary';
    default:
      return 'default';
    }
  };

  const makeAProjectFromFilteredPosts = async () => {
    const postIds = posts.map((post) => post.id);
    try {
      const newProject = {
        postIds,
        name: title,
        ownerId: profileUser.id,
        featureOnProfile: true,
        isPrivate: false,
      };
      const modal = showModal(QuickAddProject, {
        project: newProject,
        onCancel: () => {
          modal.destroy();
        },
        showOptionToFeature: true,
      });
    } catch (err) {
      enqueueSnackbar('Error creating project', { variant: 'error' });
    }
  };

  const updateCriteria = (item: ChipItem) => {
    const newCriteria = {
      ..._criteria,
      [item.type]: item.id,
    };
    const newQuery = {
      ...omit(router.query, 'profileuser'),
      [item.type]: [`${item.title}~${item.id}`],
    };
    setCriteria(newCriteria);
    if (isEmpty(omit(_criteria, 'profileuser'))) {
      setShowProjects(true);
    } else {
      setShowProjects(false);
    }
    router.push({
      pathname: `/${profileUser.username}`,
      query: newQuery,
    }, undefined, { shallow: true });
  };

  // After bulk tagging or bulk crediting, push the current route to force a reload of the posts
  const refreshView = (message?: string) => {
    // TODO: Maybe do this less forcefully in the future
    router.reload();
    setDrawer(undefined);
    if (message) enqueueSnackbar(message, { variant: 'success' });
  };

  const changeMethod = (m?: Method) => {
    if (m !== 'copy') { // reset selected
      setToCopyCredits([]);
      setToCopyTags([]);
    }
    setMethod(m);
  };

  const regenerateSummaryMutation = useMutation({
    mutationFn: () => API.regenerateProfileSummary(profileUser.id),
    onSuccess: () => {
      enqueueSnackbar('Profile summary regenerated', { variant: 'success' });
    },
    onError: (err: any) => {
      enqueueSnackbar(`Unable to regenerate profile summary: ${err?.message || err}`, { variant: 'error' });
    },
  });
  const resolvedUserSummary = regenerateSummaryMutation.data?.userSummary
    ?? profileUser.userSummary
    ?? '';

  return (
    <div>
      <div className="p-6 max-w-screen-2xl m-auto">
        <div className="flex gap-6 flex-wrap md:flex-nowrap">
          <div className="w-full md:pb-4 md:w-1/3 xl:w-1/4">
            <UserSummary
              profileUser={profileUser}
              milestoneCounts={milestoneCounts}
              totalFollowers={totalFollowers}
              onEdit={goToSettings}
              stats={stats}
              key={profileUser.id}
            />
          </div>
          <div className="w-full md:w-2/3 xl:w-3/4 pb-4">
            <div className="hidden xl:flex">
              {(isProfileOwner || canManageAccount)
                && (
                  <div className="mb-2">
                    {showImportAndExport && (
                      <Button
                        title="Generate Report"
                        aria-label="Generate Report"
                        onClick={tryToExportPosts}
                        className="gap-1"
                      >
                        <Download fontSize="small" className="mr-1" />
                        Export to CSV
                        <ProBadge height={20} />
                      </Button>
                    )}
                    {!isGondolaApp && renderMyImportsButton()}
                    <Button
                      title="Projects"
                      onClick={() => setShowProjectChips(!showProjectChips)}
                    >
                      <CreateIcon fontSize="small" className="mr-1" />
                      Projects
                    </Button>
                  </div>
                )}
            </div>
            <div ref={filterRef}>
              <Collapse in={showProjectChips}>
                <div className="m-auto text-center">
                  <p className="mb-2">
                    NEW! Organize your posts into projects! With projects you can
                    highlight your best work, or group posts by theme. You can use any of
                    the filters to get started, or start with some of these:
                  </p>
                  <div className="mb-2 space-x-3">
                    {projectChips.map((item) => (
                      <Chip
                        key={item.id}
                        label={chipLabel(item)}
                        color={chipColor(item.type)}
                        variant="outlined"
                        onClick={() => updateCriteria(item)}
                      />
                    ))}
                    <Chip label="Make a new project" color="primary" onClick={() => router.push('/projects/new')} />
                  </div>
                </div>
              </Collapse>
              <Collapse in={showFilters}>
                <div className="mb-2">
                  <FilterCriteria
                    profileUser={profileUser}
                    criteria={_criteria || {}}
                  />
                </div>
              </Collapse>
            </div>
            <div className="flex flex-col-reverse md:flex-row items-start gap-1 mb-2">
              <div>
                <FilterPostTitle
                  isLoading={isPending || isFetching}
                  headerNode={profileUser?.userSummary ? 'h4' : 'h3'}
                  title={richTitle || title}
                  hideTitle={isEmpty(omit(_criteria, 'profileuser'))}
                  showMakeProjectButton={!isEmpty(omit(_criteria, 'profileuser')) && ((userIsAdmin(currentUser) || isProfileOwner || canManageAccount))}
                  makeAProject={makeAProjectFromFilteredPosts}
                />
              </div>
              <div className="flex flex-wrap w-full md:w-auto md:items-end gap-2 md:ml-auto md:justify-end justify-start items-start my-2 md:my-0">
                <>
                  <button
                    type="button"
                    onClick={() => setShowFilters(!showFilters)}
                    className="btn-primary-outlined-xs"
                  >
                    <div className="flex items-center uppercase">
                      <Tune fontSize="small" className="mr-1" />
                      Filters
                    </div>
                  </button>
                  <SortFilter<PostSort>
                    sortOptions={currentUser?.id === profileUser?.id
                      ? postSortOptions
                      : basicPostSortOptions}
                    sort={queryToCriteria(router.query).sort as PostSort}
                    defaultSort="postedAt"
                  />
                </>
                <ShareButton text={title} />
                <button
                  type="button"
                  onClick={() => setIsImportOpen(true)}
                  className="btn-primary-outlined-xs uppercase whitespace-nowrap flex items-center"
                >
                  <AddToPhotosIcon fontSize="small" className="mr-1" />
                  Add Posts
                </button>
                {(isProfileOwner || canManageAccount) && renderBulkActionsButton()}
              </div>
            </div>

            <div className="w-full overflow-hidden">
              {showProjects && profileUser?.hasFeaturedProjects && (
                <Portfolio profileUser={profileUser} />
              )}
              {statsMeta && !profileUser?.areStatsHidden && !showProjects && (
                <div className="md:text-lg mb-2">
                  <PostStats
                    stats={statsMeta}
                    isLoading={isPending || isFetching}
                    error={metaError}
                  />
                </div>
              )}

              <div>
                {drawer === 'match' ? (
                  <MatchDrawer
                    count={selectedPostIds.length}
                    drawer={drawer}
                    onCancel={() => setDrawer(undefined)}
                    posts={[
                      ...posts.filter((p) => selectedPostIds.includes(p.id)),
                      ...selectedSimilarPosts,
                    ]}
                    left={0}
                  >
                    <div className="px-6">
                      <SelectedPosts
                        title={`The following ${selectedPostIds.length} ${pluralize('post', selectedPostIds.length)} will be added to the match`}
                        posts={[
                          ...posts.filter((p) => selectedPostIds.includes(p.id)),
                          ...selectedSimilarPosts,
                        ]}
                        selectedPostIds={selectedPostIds}
                        onChange={setSelectedPostIds}
                      />
                      <SimilarPosts
                        posts={similarPosts}
                        onSelect={(post: Post) => {
                          setSelectedPostIds([...selectedPostIds, post.id]);
                          setSelectedSimilarPosts([...selectedSimilarPosts, post]);
                        }}
                      />
                    </div>
                  </MatchDrawer>
                ) : (
                  <AddDrawer
                    count={selectedPostIds.length}
                    drawer={drawer}
                    method={method}
                    onChangeMethod={changeMethod}
                    onCancel={() => setDrawer(undefined)}
                    left={0}
                  >
                    <div className="px-6">
                      {drawer === 'credit' ? (
                        <>
                          <CreditDrawer
                            postIds={selectedPostIds}
                            credits={toCopyCredits}
                            method={method}
                            onCancel={() => setMethod(undefined)}
                            onChange={refreshView}
                          />
                          <SelectedPosts
                            title={`Credits will be applied to the following ${selectedPostIds.length} ${pluralize('post', selectedPostIds.length)}`}
                            posts={posts.filter((p) => selectedPostIds.includes(p.id))}
                            selectedPostIds={selectedPostIds}
                            onChange={setSelectedPostIds}
                          />
                        </>
                      ) : (
                        <>
                          <AddTags
                            postIds={selectedPostIds}
                            copyTags={toCopyTags}
                            method={method}
                            onChange={refreshView}
                            onCancel={() => setMethod(undefined)}
                          />
                          <SelectedPosts
                            title={`Tags will be applied to the following ${selectedPostIds.length} ${pluralize('post', selectedPostIds.length)}`}
                            posts={posts.filter((p) => selectedPostIds.includes(p.id))}
                            selectedPostIds={selectedPostIds}
                            onChange={setSelectedPostIds}
                          />
                        </>
                      )}
                    </div>
                  </AddDrawer>
                )}
              </div>
              {postView === 'card' ? (
                <FilterPostResult
                  posts={posts}
                  pinnedPosts={profileUserPins}
                  isLoading={isPending || isFetching}
                  isProfileOwner={isProfileOwner}
                  error={postsError || undefined}
                  onPinChange={() => fetchPins()}
                  ignoreEmptyState={additionalPosts.length > 0}
                />
              ) : (
                <div className="w-full overflow-hidden">
                  <Manager
                    posts={posts}
                    isLoading={isPending || isFetching}
                    error={postsError || undefined}
                    selectedPostIds={selectedPostIds}
                    setSelectedPostIds={setSelectedPostIds}
                    drawer={drawer}
                    method={method}
                    setDrawer={setDrawer}
                    setSimilarPosts={setSimilarPosts}
                    setToCopyTags={setToCopyTags}
                    setToCopyCredits={setToCopyCredits}
                    isProfileOwner={isProfileOwner}
                  />
                </div>
              )}
              {!!pagingMeta?.count && !!posts.length && (
                <Pagination paging={pagingMeta} isLoading={isPending || isFetching} />
              )}
              {postView === 'card' && additionalPosts.length > 0 && (
                <div>
                  {posts.length > 0 && (
                    <>
                      <Divider className="mb-4" />
                      <h3 className="text-xl font-bold mb-4 uppercase">
                        View more from
                        {' '}
                        @
                        {profileUser.username}
                      </h3>
                    </>
                  )}
                  <FilterPostResult
                    posts={additionalPosts}
                    pinnedPosts={profileUserPins}
                    isLoading={additionalLoading && !additionalPosts?.length}
                    isProfileOwner={isProfileOwner}
                    error={undefined}
                    onPinChange={() => fetchPins()}
                  />
                  {additionalPosts.length >= 4 && (
                    <button
                      type="button"
                      onClick={() => router.push({
                        pathname: `/${profileUser.username}`,
                        query: { creditedStatus: 'uncredited' },
                      })}
                      className="btn-primary-outlined m-4 align-middle justify-center"
                    >
                      to view all
                      {' '}
                      {uncreditedTotal}
                      {' '}
                      of
                      {' '}
                      @
                      {profileUser.username}
                      &apos;s uncredited posts click here
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {isImportOpen && <ImportWizard onClose={() => setIsImportOpen(false)} />}
        {(resolvedUserSummary || userIsAdmin(currentUser)) && (
          <div className="mx-5 text-center border-t pt-3">
            {!!resolvedUserSummary && (
              <h3 className="text-m">
                {resolvedUserSummary}
              </h3>
            )}
            {userIsAdmin(currentUser) && (
              <div className="flex justify-center mt-2">
                <button
                  type="button"
                  className={`btn-primary-outlined-xs uppercase inline-flex items-center justify-center gap-1 ${regenerateSummaryMutation.isPending ? ' opacity-60 cursor-not-allowed' : ''}`}
                  onClick={() => regenerateSummaryMutation.mutate()}
                  disabled={regenerateSummaryMutation.isPending
                    || isPending || isFetching || additionalLoading}
                >
                  <RefreshIcon fontSize="small" className="shrink-0" />
                  {regenerateSummaryMutation.isPending ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            )}
          </div>
        )}
        {currentUser?.isAdmin && (
          <div className="mx-auto mt-6 w-full max-w-5xl px-4 hidden lg:block">
            <div className="flex justify-center">
              <CreditContributions activity={creditActivity} />
            </div>
          </div>
        )}
      </div>
      <Modal
        open={showAutoCreditModal}
        onCancel={() => setShowAutoCreditModal(false)}
        maxWidth="sm"
      >
        <div className="text-center text-xl">
          You have
          {' '}
          <strong>
            {suggestedCreditCount?.count}
          </strong>
          {' '}
          uncredited posts
          <br />
          <button
            type="button"
            className="btn-secondary-outlined m-2"
            onClick={() => router.push('/settings/accounts')}
          >
            Auto-credit these and future posts
          </button>
          <button
            type="button"
            className="btn-secondary-outlined"
            onClick={() => {
              router.push({
                pathname: `/${profileUser.username}`,
                query: { creditedStatus: 'suggested' },
              });
              setShowAutoCreditModal(false);
            }}
          >
            View all of your suggested posts
          </button>
        </div>
      </Modal>
    </div>
  );
};
